import { toast } from "@birdwatch/ui/toast";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "@tanstack/react-router";
import { cn } from "cn";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BwLoadingLine } from "@/components/ui/icons";
import { APP_ROUTERS } from "@/constants/app-routers";
import {
  AddressCard,
  CommitmentCard,
  PaymentStatusModal,
  type UpdatePurchaseIntentInput,
  useCheckout,
  useUpdatePurchaseIntent,
} from "@/features/my/checkout/shell";
import {
  formatLaunchDate,
  formatLaunchDateWithYear,
  PRESALE_CAMPAIGN,
  PRESALE_PURCHASE_INTENT_FLOW,
} from "@/features/shared/presale";
import { PRESALE_EVENTS, useAnalytics } from "@/libs/analytics";
import { usePresaleCart } from "./_api/use-presale-cart";
import { usePresaleStandardPrice } from "./_api/use-presale-standard-price";
import { PresaleMembershipCard } from "./_components/checkout/presale-membership-card";
import { PresalePaymentMethodSection } from "./_components/checkout/presale-payment-method";
import { PresaleServiceCreditCard } from "./_components/checkout/presale-service-credit-card";
import { PresaleSummaryCard } from "./_components/checkout/presale-summary-card";
import { PresaleFunnelLayout } from "./_components/funnel/presale-funnel-layout";
import { getPresaleAnalyticsProps } from "./_libs/presale-analytics-props";
import {
  keepCampaignSearch,
  type PresaleCheckoutSearch,
} from "./_libs/presale-funnel";

/**
 * Step 3 — Confirm & Checkout.
 *
 * ONE server resource backs this page: the `PurchaseIntent` step 2 created, read through
 * `usePresaleCart`. `payload.priceDetail` is the ledger, `payload.checkoutLineItems` is
 * the cart, `placeId` finds the home and `accountId` the saved cards — the same shape the
 * member checkout uses. The funnel used to rebuild all of it from the checkout-link
 * catalog and price it in the browser, which meant re-implementing Stripe's coupon math
 * and quoting a total `POST /checkout/pay` need not agree with.
 *
 * The cart itself is not chosen here. The invitation link carries `checkoutLinkId` and
 * step 2 bound the intent to it through `add-my-place`; this page restates that tier
 * read-only, so the only choices left are the promo code and the card — and both are
 * written to the intent as they are made, because the server prices from them.
 */
export function PresaleCheckoutPage({
  search,
}: {
  search: PresaleCheckoutSearch;
}) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useUser();
  const launchDateLabel = formatLaunchDate(PRESALE_CAMPAIGN.launchDate);
  const launchDateWithYearLabel = formatLaunchDateWithYear(
    PRESALE_CAMPAIGN.launchDate,
  );

  const { cart, isLoading, isFetching, isError } = usePresaleCart(
    search.purchaseIntentId,
  );
  const updatePurchaseIntent = useUpdatePurchaseIntent();
  const checkout = useCheckout();
  const { sendEvent } = useAnalytics();
  // What the membership costs AFTER launch. The cart only ever carries the link's $0/mo
  // presale override, so the summary needs the catalog price too — read off the plan the
  // intent is bound to, which is the plan that will be charged.
  const { data: standardPrice } = usePresaleStandardPrice(cart?.planKey);

  /** `"card"` / `"us_bank_account"` — the analytics `paymentMethod` key, as Join sends it. */
  const [paymentMethodType, setPaymentMethodType] = useState<string>();
  const [agreed, setAgreed] = useState(false);
  const [modalStatus, setModalStatus] = useState<
    "processing" | "success" | "failed" | null
  >(null);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    navigate({ to: APP_ROUTERS.PRESALE_SIGN_UP, replace: true });
  }, [isLoaded, isSignedIn]);

  /**
   * No reservation in the URL — send them to step 2 rather than dead-ending here.
   *
   * That step is where `usePresaleResume` runs, so a buyer who already has an unfinished
   * reservation is bounced straight back with its id attached. `usePresaleCart` is gated
   * on the same id, so nothing is requested on the way through.
   */
  useEffect(() => {
    if (search.purchaseIntentId) return;
    navigate({
      to: APP_ROUTERS.PRESALE_PLACE,
      search: keepCampaignSearch(search),
      replace: true,
    });
  }, [search.purchaseIntentId]);

  /**
   * Already charged — the reservation is done.
   *
   * `stripeInvoiceId` is set by `POST /checkout/pay`, so without this a buyer returning to
   * a paid reservation (a back button, a stale tab) gets a live Confirm button over a cart
   * they have already bought. The member checkout guards the same way.
   */
  useEffect(() => {
    if (!cart?.stripeInvoiceId) return;
    navigate({
      to: APP_ROUTERS.PRESALE_SUCCESS,
      search: keepCampaignSearch(search),
      replace: true,
    });
  }, [cart?.stripeInvoiceId]);

  /**
   * Memoized because `handlePaymentSuccess` below depends on it: `PaymentStatusModal` keeps
   * `onSuccess` in its own timer's effect deps, so an identity that churns every render
   * would restart that timer and the success handler could never fire. `cart` is memoized
   * at its source for exactly this.
   */
  const analyticsProps = useMemo(
    () => getPresaleAnalyticsProps({ search, cart }),
    [search, cart],
  );

  /** Records a choice on the real purchase intent. Silent when there is none yet. */
  const persist = async (input: UpdatePurchaseIntentInput) => {
    if (!cart) return;
    await updatePurchaseIntent.mutateAsync({
      purchaseIntentId: cart.intentId,
      updatePurchaseIntentInput: input,
    });
  };

  /**
   * Re-stamps a reservation whose `flow` write didn't land.
   *
   * `usePresalePlace().reserve` stamps it best-effort — it must not fail a place that was
   * already created — and `findPresaleResumes` reads exactly that field, so a missed stamp
   * would leave this reservation unresumable and the buyer adding the home a second time.
   * `flow` is a top-level field on the intent (`getPurchaseIntentById` lifts it out of the
   * payload), so the happy path fires no request. One attempt per mount, and on the intent
   * this page READ — there is no other reservation it could target now.
   */
  const stampedFlow = useRef(false);
  useEffect(() => {
    if (stampedFlow.current || !cart) return;
    if (cart.flow === PRESALE_PURCHASE_INTENT_FLOW) return;
    stampedFlow.current = true;
    void persist({ flow: PRESALE_PURCHASE_INTENT_FLOW }).catch(() => {
      // Same reasoning as `reserve` — a buyer mid-checkout must not be blocked by this.
    });
  }, [cart]);

  /**
   * Cart viewed, once per mount, on a SETTLED cart.
   *
   * The 1s debounce is what keeps the event quoting the price the buyer is actually
   * looking at: applying a promo code or switching card re-prices the cart server-side,
   * and each change restarts the window so the settled figures are the ones reported.
   */
  const cartViewedSentRef = useRef(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!cart?.priceDetail || cartViewedSentRef.current) return;
      cartViewedSentRef.current = true;
      sendEvent(PRESALE_EVENTS.INITIATE_CHECKOUT, {
        ...analyticsProps,
        stepId: "checkout",
        stepName: "Checkout",
      });
    }, 1000);
    return () => clearTimeout(timeout);
    // `sendEvent` is deliberately absent: the analytics context value is rebuilt on every
    // provider render, so including it would let an unrelated render reset this timer.
  }, [cart]);

  /**
   * Records the chosen card on the intent immediately, not at Confirm.
   *
   * `dueToday` and the convenience-fee row are computed server-side FROM this id — the fee
   * applies only to a credit card (`FeeCalculator.shouldApplyProcessingFees`) — so a cart
   * priced before the selection landed would quote a fee-free total and then charge the
   * fee. The shared mutation patches the id into the detail cache optimistically, so the
   * radio does not snap back while the re-price is in flight.
   */
  const handleSelectPaymentMethod = (id: string, type?: string) => {
    setPaymentMethodType(type);
    void persist({ paymentMethodId: id }).catch(() => {
      toast.error({
        title: "We couldn't save that payment method. Please retry.",
      });
    });
  };

  const handleConfirm = async () => {
    // Nothing to charge without a reservation — `persist` silently no-ops on a missing
    // one, so without this guard the modal would report success on a purchase that never
    // existed.
    if (!cart) {
      toast.error({
        title: "We couldn't find your reservation. Please start again.",
      });
      return;
    }

    setModalStatus("processing");
    try {
      // Every choice is already on the intent — the promo code through `PromoCode`, the
      // card through `handleSelectPaymentMethod` — and `POST /checkout/pay` reads all of
      // it from there, so there is nothing left to write before charging.
      // The generated client throws CustomApiError on a non-2xx, so a returned
      // value means the charge succeeded.
      await checkout.mutateAsync(cart.intentId);

      sendEvent(PRESALE_EVENTS.PURCHASE, {
        ...analyticsProps,
        stepId: "checkout",
        stepName: "Checkout",
        paymentMethod: paymentMethodType ?? cart.paymentMethodType,
        success: true,
      });

      setModalStatus("success");
    } catch (error) {
      sendEvent(PRESALE_EVENTS.PURCHASE, {
        ...analyticsProps,
        stepId: "checkout",
        stepName: "Checkout",
        paymentMethod: paymentMethodType ?? cart.paymentMethodType,
        success: false,
        paymentError: error instanceof Error ? error.message : undefined,
      });
      setModalStatus("failed");
    }
  };

  /**
   * The confirmed reservation, fired HERE rather than on `/presale/success`: this step is
   * where the cart and the intent id both exist, and the confirmation page both loses
   * `purchaseIntentId` (`keepCampaignSearch` does not carry it) and is reachable by
   * signed-out strangers who must not be counted as buyers.
   *
   * `useCallback` is load-bearing, not tidiness: `PaymentStatusModal` keeps `onSuccess` in
   * its 1500ms timer's effect deps, so a fresh inline arrow each render restarts the timer
   * — and a re-render cadence under 1.5s would mean neither this event NOR the navigation
   * ever fires.
   */
  const handlePaymentSuccess = useCallback(() => {
    sendEvent(PRESALE_EVENTS.SUBSCRIBE, {
      ...analyticsProps,
      stepId: "subscribe",
      stepName: "Subscribe",
    });
    navigate({
      to: APP_ROUTERS.PRESALE_SUCCESS,
      search: keepCampaignSearch(search),
    });
  }, [analyticsProps, navigate, search, sendEvent]);

  if (isLoading) {
    return (
      <PresaleFunnelLayout step="checkout">
        <div className="flex justify-center py-24">
          <BwLoadingLine size={28} className="animate-spin text-text-primary" />
        </div>
      </PresaleFunnelLayout>
    );
  }

  // The reservation could not be read — say so rather than rendering an empty cart with a
  // dead Confirm button. No offer is resolved on this page any more, so this is about the
  // reservation itself, not about the campaign's offers.
  if (isError || !cart) {
    return (
      <PresaleFunnelLayout
        step="checkout"
        title="We couldn’t load your reservation"
        description={`Your founding spot in ${PRESALE_CAMPAIGN.marketName} couldn’t be loaded. Please use the link from your invitation, or contact us and we’ll help.`}
      >
        {null}
      </PresaleFunnelLayout>
    );
  }

  return (
    <PresaleFunnelLayout
      step="checkout"
      wide
      title="Confirm your Founding Membership"
      description="Just one final step."
    >
      <div
        className={cn(
          /* sorted */
          "grid grid-cols-1 md:grid-cols-5",
          "md:gap-x-6 lg:gap-x-8",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col space-y-6 md:col-span-3">
          <div>
            <h2 className="text-base leading-normal font-medium text-text-dark-max">
              Confirm your service
            </h2>
            <p className="text-sm leading-5 font-normal text-text-dark-low">
              Review your address and reserve your founding spot
            </p>
          </div>

          <AddressCard />

          <PresaleMembershipCard
            membership={cart.membership}
            services={cart.services}
            launchDateLabel={launchDateLabel}
          />

          <PresaleServiceCreditCard credit={cart.credit} />

          <CommitmentCard agreed={agreed} setAgreed={setAgreed} />

          <PresalePaymentMethodSection
            accountId={cart.accountId || undefined}
            selectedId={cart.paymentMethodId}
            onSelect={handleSelectPaymentMethod}
            onAdded={(id, type) =>
              sendEvent(PRESALE_EVENTS.PAYMENT_METHOD_ADDED, {
                ...analyticsProps,
                stepId: "payment_method",
                stepName: "Payment Method Added",
                paymentMethod: type,
                paymentMethodId: id,
              })
            }
            onChanged={(id, type) =>
              sendEvent(PRESALE_EVENTS.PAYMENT_METHOD_CHANGED, {
                ...analyticsProps,
                stepId: "payment_method",
                stepName: "Payment Method Changed",
                paymentMethod: type,
                paymentMethodId: id,
                // Join's `PaymentMethodChanged` is a Stripe PaymentElement FORM-change
                // event; presale's is a saved-card selection. Same name, different act —
                // this key is what lets one query tell them apart.
                paymentMethodSource: "saved_card_selection",
              })
            }
            onAddFailed={(message) =>
              sendEvent(PRESALE_EVENTS.PAYMENT_METHOD_ADD_FAILED, {
                ...analyticsProps,
                stepId: "payment_method",
                stepName: "Payment Method Add Failed",
                paymentMethodError: message,
              })
            }
          />
        </div>

        <div className="min-w-0 md:col-span-2">
          <div className="sticky top-17 mt-8 flex justify-center md:mt-0 md:justify-end">
            <PresaleSummaryCard
              priceDetail={cart.priceDetail}
              launchDateLabel={launchDateLabel}
              launchDateWithYearLabel={launchDateWithYearLabel}
              standardPrice={standardPrice}
              onConfirm={handleConfirm}
              isConfirming={modalStatus === "processing"}
              // Never offer to charge a total the server has already superseded. Both
              // remaining choices re-price the cart server-side — the promo code through
              // `PromoCode`, the card through `handleSelectPaymentMethod` — so `isFetching`
              // covers the re-price landing (Join's own guard) and `isPending` the write
              // that triggered it. The latter is what closes the card-switch window: the
              // optimistic `paymentMethodId` patch re-enables this button the instant the
              // write starts, on a `Due today` that has not been recomputed yet.
              disabled={
                !agreed ||
                !cart.paymentMethodId ||
                isFetching ||
                updatePurchaseIntent.isPending
              }
            />
          </div>
        </div>
      </div>

      <PaymentStatusModal
        open={modalStatus !== null}
        onOpenChange={(open) => !open && setModalStatus(null)}
        status={modalStatus ?? "processing"}
        onSuccess={handlePaymentSuccess}
        onCtaClick={() => setModalStatus(null)}
      />
    </PresaleFunnelLayout>
  );
}
